"use client";

import { Award, Backpack, FileCheck, Flag, Mountain, Radio, Wind } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Visit() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: stepsRef,
    isVisible: stepsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: includedRef,
    isVisible: includedVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const steps = [{
    number: "01",
    title: "CONSULTATION",
    Icon: FileCheck,
    description: "We review your medical history, current symptoms, and previous diagnostic reports to prepare for your visit."
  }, {
    number: "02",
    title: "EVALUATION",
    Icon: Backpack,
    description: "Comprehensive physical examination and clinical assessment conducted by Dr. Carrillo-Jimenez."
  }, {
    number: "03",
    title: "DIAGNOSTICS",
    Icon: Wind,
    description: "Advanced on-site testing including EKG, echocardiogram, and stress testing for precise heart health data."
  }, {
    number: "04",
    title: "PLANNING",
    Icon: Mountain,
    description: "Development of a personalized cardiovascular care strategy tailored to your specific health goals."
  }, {
    number: "05",
    title: "PROCEDURE",
    Icon: Flag,
    description: "Execution of interventional or vascular procedures using state-of-the-art medical technology."
  }, {
    number: "06",
    title: "FOLLOW-UP",
    Icon: Award,
    description: "Ongoing monitoring, medication management, and review of your long-term cardiovascular wellness."
  }];
  const included = ["Initial clinical consultation", "Comprehensive cardiac evaluation", "On-site diagnostic testing", "Personalized treatment roadmap", "Coordinated follow-up care", "Post-procedure recovery support", "Contact our office to verify insurance coverage"];
  return <section data-section-id="2290" id="preparing-for-your-visit" className="bg-background text-foreground pt-24 pb-12 px-6 lg:px-12 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        {}
        <div ref={headerRef} className={`mb-20 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-primary" />
            <p className="font-default text-xs tracking-[0.3em] text-primary uppercase">
              [ Clinical Process ]
            </p>
          </div>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-6 leading-[0.9]">
            Your Care Journey
          </h2>
          <p className="font-default text-lg md:text-xl text-soft-gray max-w-2xl leading-relaxed">
            A six-stage clinical protocol engineered for precision cardiovascular care. Every phase is documented, evidence-based, and patient-centered.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-12">
          {}
          <div ref={stepsRef} className="relative">
            {}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px -translate-y-1/2 pointer-events-none" style={{
            backgroundImage: "repeating-linear-gradient(to right, var(--color-gray-dark) 0, var(--color-gray-dark) 8px, transparent 8px, transparent 16px)",
            zIndex: 0
          }} aria-hidden="true" />

            {}
            <div className="lg:hidden absolute top-0 bottom-0 left-8 w-px pointer-events-none" style={{
            backgroundImage: "repeating-linear-gradient(to bottom, var(--color-gray-dark) 0, var(--color-gray-dark) 8px, transparent 8px, transparent 16px)",
            zIndex: 0
          }} aria-hidden="true" />

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 relative z-10">
              {steps.map((step, i) => {
              const Icon = step.Icon;
              return <Card data-index={i} key={step.number} className={`relative bg-secondary backdrop-blur-md border border-border shadow-lg py-8 px-6 gap-5 transition-all duration-700 ${stepsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
                transitionDelay: `${i * 100}ms`
              }}>
                    {}
                    <div className="hidden lg:block absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-primary" aria-hidden="true" />
                    <div className="lg:hidden absolute top-10 -left-[1.65rem] w-3 h-3 bg-primary" aria-hidden="true" />

                    {}
                    <div className="font-default text-5xl md:text-6xl font-bold text-primary leading-none">
                      {step.number}
                    </div>

                    {}
                    <div className="flex items-center justify-start">
                      <Icon className="size-9 text-white" strokeWidth={1.5} aria-hidden="true" />
                    </div>

                    {}
                    <h3 className="font-default font-bold text-xl uppercase tracking-wide text-white">
                      {step.title}
                    </h3>

                    {}
                    <p className="font-default text-sm leading-relaxed text-soft-gray">
                      {step.description}
                    </p>

                    {}
                    <div className="h-px w-12 bg-primary/60 mt-2" />
                  </Card>;
            })}
            </div>
          </div>

          {}
          <div ref={includedRef} className="lg:relative">
            <div className="lg:sticky lg:top-24">
              <Card className={`bg-secondary backdrop-blur-md border border-border shadow-lg py-8 px-7 gap-5 transition-all duration-700 ${includedVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
                <div className="flex items-center gap-3 pb-4 border-b border-border">
                  <Radio className="size-5 text-primary" strokeWidth={1.5} aria-hidden="true" />
                  <p className="font-default text-xs tracking-[0.25em] text-primary uppercase">
                    Patient Manifest
                  </p>
                </div>

                <h3 className="font-default font-bold text-2xl uppercase tracking-tight text-white">
                  What's Included
                </h3>

                <p className="font-default text-sm text-soft-gray leading-relaxed">
                  Every patient visit is structured for clarity and clinical excellence.
                </p>

                <ul className="flex flex-col gap-4 mt-2">
                  {included.map((item, i) => <li data-index={i} key={item} className="flex items-start gap-3">
                      <Checkbox checked disabled className="mt-0.5 size-5 border-primary data-[state=checked]:bg-primary data-[state=checked]:text-white data-[state=checked]:border-primary disabled:opacity-100 disabled:cursor-default" aria-label={item} />
                      <span className="font-default text-sm text-white leading-snug">
                        {item}
                      </span>
                    </li>)}
                </ul>

                <div className="mt-4 pt-5 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="font-default text-xs tracking-widest text-soft-gray uppercase">
                      Status
                    </span>
                    <span className="flex items-center gap-2 font-default text-xs tracking-widest text-primary uppercase">
                      <span className="size-2 bg-primary animate-pulse" aria-hidden="true" />
                      Active
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
